import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Box, Button, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Paper, Stack, Divider, Alert, DialogContentText } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FlexColumnBox from "../../components/common/FlexBox";
import { Check } from "@mui/icons-material";
import { addDaysToDate, getNextPeriod } from "../../utils/dateUtils";
import WeekTableForecast, {
  WeekData,
  DailyData,
} from "../../components/common/weekTableForecast";
import { useHotels } from "../../hooks/useHotels";
import { Hotel } from "../../types/hotel.d";
import HotelSelector from "../../components/common/HotelSelector";
import { useAuth } from "../../context/authContext";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import { StatService } from "../../api/statService";
import { ForecastItem, ForecastUpdateDto } from "../../types/stat.d";
import WeekDatePicker from "../../components/common/WeekDatePicker";
import { buildPrevisionsCsvExport, buildPrevisionsCsvTemplate } from "../../utils/previsionsCsv";
import { ExcelHotelData, getCellValue, importDataFromExcel, parseExcelDate } from "../../utils/importDataFromExcel";

const resetDateToFriday = (date: Date) => {
  const dayOfWeek = date.getDay();
  const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
  const adjustedDate = new Date(date);
  adjustedDate.setDate(adjustedDate.getDate() + daysUntilFriday);
  adjustedDate.setHours(0, 0, 0, 0);
  return adjustedDate;
};

const Previsions = () => {
  const excelImportStartCell = 'A27';
  const excelImportEndCell = 'C41';
  const maxUnitsCell = 'C21';

  const dateColumn = 'A';
  const soldUnitsColumn = 'C';

  const initialStartDate = getNextPeriod(new Date);
  
  const [localStartDate, setLocalStartDate] = useState<Date>(initialStartDate);
  
  const [startDate, setStartDate] = useState<Date>(initialStartDate);
  const endDate = useMemo(() => addDaysToDate(startDate, 13), [startDate]);
  
  const [isLoading, setIsLoading] = useState(false);
  const hotels = useHotels();
  const { user } = useAuth();

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const applyDate = useCallback((date: Date) => {
    setStartDate(date);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const debouncedSetDate = useCallback((date: Date) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setStartDate(date);
    }, 3000);
  }, []);

  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const [week1Data, setWeek1Data] = useState<WeekData>({});
  const [week2Data, setWeek2Data] = useState<WeekData>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState<
    "success" | "error" | "confirm" | null
  >(null);
  const [modalMessage, setModalMessage] = useState<string | undefined>();
  const [importOpen, setImportOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [excelImportError, setExcelImportError] = useState(false);
  const [excelImportSuccess, setExcelImportSuccess] = useState(false);
  
  const skipNextDataUpdate = useRef(false);

  const initializeWeeklyData = (maxUnitsCapacity: number) => {
    const initialData = Array.from({ length: 7 }).reduce(
      (acc: WeekData, _, day) => {
        acc[day] = { chambresAVendre: maxUnitsCapacity, chambresVendues: 0 };
        return acc;
      },
      {} as WeekData
    );
    setWeek1Data(initialData);
    setWeek2Data(initialData);
  };

  useEffect(() => {
    if (user?.role !== "Admin" && user?.hotel) {
      setSelectedHotel(user.hotel);
      initializeWeeklyData(user.hotel.maxUnitsCapacity);
    }
  }, [user]);

  useEffect(() => {
    if(skipNextDataUpdate.current) {
      skipNextDataUpdate.current = false;
      return;
    }

    setWeek1Data({});
    setWeek2Data({});

    const fetchForecastData = async (hotelId: number) => {
      try {
        const data = await StatService.getForecast(hotelId, startDate, endDate);
        if (!selectedHotel) return;

        if (!data || data.length === 0) {
          initializeWeeklyData(selectedHotel.maxUnitsCapacity);
          return;
        }

        const week1EndDate = addDaysToDate(startDate, 6);
        const week2StartDate = addDaysToDate(startDate, 7);
        
        const startDateStr = startDate.toISOString().split("T")[0];
        const week1EndDateStr = week1EndDate.toISOString().split("T")[0];
        const week2StartDateStr = week2StartDate.toISOString().split("T")[0];
        const endDateStr = endDate.toISOString().split("T")[0];
        
        const week1 = data.filter((d) => {
          return d.date >= startDateStr && d.date <= week1EndDateStr;
        });
        
        const week2 = data.filter((d) => {
          return d.date >= week2StartDateStr && d.date <= endDateStr;
        });

        const formatWeekData = (
          week: ForecastItem[],
          periodStartDate: Date,
          maxUnitsCapacity: number
        ): WeekData => {
          const result: WeekData = {};

          for (let i = 0; i < 7; i++) {
            const currentDate = addDaysToDate(periodStartDate, i);
            const currentDateString = currentDate.toISOString().split("T")[0];

            const forecast = week.find(
              (item) => item.date === currentDateString
            );

            const availableUnits =
              forecast?.available_Units ?? maxUnitsCapacity;
            const soldUnits = forecast?.sold_Units ?? 0;

            const pourcentageVendues =
              availableUnits > 0
                ? Math.round((soldUnits / availableUnits) * 1000) / 10
                : undefined;

            result[i] = {
              chambresAVendre: availableUnits,
              chambresVendues: soldUnits,
              pourcentageVendues,
              tauxOccupation:
                pourcentageVendues !== undefined
                  ? `${pourcentageVendues}%`
                  : "-",
            };
          }

          return result;
        };

        setWeek1Data(
          formatWeekData(week1, startDate, selectedHotel.maxUnitsCapacity)
        );
        setWeek2Data(
          formatWeekData(
            week2,
            addDaysToDate(startDate, 7),
            selectedHotel.maxUnitsCapacity
          )
        );
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
      }
    };

    if (selectedHotel) {
      fetchForecastData(selectedHotel.id);
    }
  }, [selectedHotel, startDate, endDate]);

  const handleSend = async () => {
    if (!selectedHotel) return alert("Veuillez sélectionner un hôtelier !");
    setIsLoading(true);

    const transformWeekData = (
      weekData: WeekData,
      period: number
    ): ForecastUpdateDto[] => {
      return Object.entries(weekData).map(([dayIndex, data]) => {
        const date = addDaysToDate(
          startDate,
          parseInt(dayIndex) + (period === 2 ? 7 : 0)
        );
        return {
          hotelId: selectedHotel.id,
          sold_Units: data.chambresVendues || 0,
          available_Units: data.chambresAVendre || 0,
          period,
          date: date.toISOString().split("T")[0],
        };
      });
    };

    const combinedData = [
      ...transformWeekData(week1Data, 1),
      ...transformWeekData(week2Data, 2),
    ];

    try {
      await StatService.sendForecast(combinedData);
      setModalStatus("success");
      setModalMessage("Les prévisions ont été envoyées avec succès !");
    } catch (error) {
      console.error("Erreur lors de l'envoi :", error);
      setModalStatus("error");
      setModalMessage(
        "Une erreur est survenue lors de l'envoi des prévisions."
      );
    } finally {
      setIsLoading(false);
      setModalOpen(true);
    }
  };

  const handleConfirm = () => {
    setModalStatus("confirm");
    setModalMessage("Êtes-vous sûr de vouloir envoyer ces prévisions ?");
    setModalOpen(true);
  };

  const handleDataChange = (
    week: number,
    day: number,
    updatedDayData: DailyData
  ) => {
    if (week === 1) {
      setWeek1Data((prev) => ({ ...prev, [day]: updatedDayData }));
    } else {
      setWeek2Data((prev) => ({ ...prev, [day]: updatedDayData }));
    }
  };

  const handleHotelChange = useCallback((hotel: Hotel | null) => {
    setSelectedHotel(hotel);
    if (hotel) initializeWeeklyData(hotel.maxUnitsCapacity);
  }, []);

  const handleExportCsv = () => {
    const csv = buildPrevisionsCsvExport(startDate, week1Data, week2Data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = addDaysToDate(startDate, 13).toISOString().slice(0, 10);
    a.download = `previsions_${startStr}_${endStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadTemplate = () => {
    const csv = buildPrevisionsCsvTemplate(startDate);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const startStr = startDate.toISOString().slice(0, 10);
    const endStr = addDaysToDate(startDate, 13).toISOString().slice(0, 10);
    a.download = `PREVISIONS_GABARIT_${startStr}_${endStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onFileSelected = async (file: File | null) => {
    const allowedFileExtension = 'xlsx';

    if (!file || file.name.split('.').pop() !== allowedFileExtension) return;

    const buffer = await file.arrayBuffer();

    const data = await importDataFromExcel(buffer, excelImportStartCell, excelImportEndCell);
    const maxUnits = await getCellValue(buffer, maxUnitsCell);

    if(typeof data !== "boolean" && typeof maxUnits !== "boolean")
      excelImport(data, parseInt(maxUnits));
  };

  const excelImport = (data: ExcelHotelData, maxUnits : number) => {
    skipNextDataUpdate.current = true;
    // Trouver la date de départ et l'utiliser
    const startDate = Object.values(data[dateColumn])[0];
    
    const date = parseExcelDate(startDate);
    
    if(typeof(date) === "boolean")
      setExcelImportError(true);
    else {
      setStartDate(date);
      setLocalStartDate(date);
    }
    
    // Importer les données
    if(Object.values(data[soldUnitsColumn]).length !== 15) {
      setExcelImportError(true);
      return;
    }

    const importedWeek1Data : WeekData = {};
    const importedWeek2Data : WeekData = {};

    let day = 0;
    let week = 1;

    for(const val of Object.values(data[soldUnitsColumn])) {
      const percentageSold = Math.round(parseInt(val) / maxUnits * 100 * 10) / 10;

      if(val == '') {
        week = 2;
        day = 0;
        continue;
      }

      if(week == 1) {
        importedWeek1Data[day] = {};
        importedWeek1Data[day].chambresAVendre = maxUnits;
        importedWeek1Data[day].tauxOccupation = percentageSold + '%';
        importedWeek1Data[day++].chambresVendues = parseInt(val);
      }
      if(week == 2) {
        importedWeek2Data[day] = {};
        importedWeek2Data[day].chambresAVendre = maxUnits;
        importedWeek2Data[day].tauxOccupation = percentageSold + '%';
        importedWeek2Data[day++].chambresVendues = parseInt(val);
      }
    }

    setWeek1Data(importedWeek1Data);
    setWeek2Data(importedWeek2Data);

    setImportOpen(false);
    setExcelImportSuccess(true);
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) onFileSelected(file);
  };

  return (
    <FlexColumnBox margin="auto" mt={5} mb={5} alignItems="center" gap={5}>
      <Box
        width="90%"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
          Prévisions d'occupation
        </Typography>
        <Box sx={{ position: "absolute", right: 0, display: "flex", gap: 1 }}>
          <Tooltip title="Importer Excel">
            <span>
              <IconButton color="primary" onClick={() => setImportOpen(true)} size="large" disabled={!selectedHotel}>
                <FileUploadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Exporter CSV">
            <span>
              <IconButton color="primary" onClick={handleExportCsv} size="large" disabled={!selectedHotel}>
                <FileDownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        display="flex"
        gap={2}
        mb={4}
        flexWrap="wrap"
        justifyContent="center"
      >
        {user?.role === "Admin" && (
          <HotelSelector
            hotels={hotels}
            selectedHotel={selectedHotel}
            onChange={handleHotelChange}
          />
        )}
        <WeekDatePicker
          startDate={localStartDate}
          setStartDate={(date) => {
            const aligned = resetDateToFriday(date);
            setLocalStartDate(aligned);
            debouncedSetDate(aligned);
          }}
          onApply={(date) => {
            const aligned = resetDateToFriday(date);
            setLocalStartDate(aligned);
            applyDate(aligned);
          }}
        />
      </Box>

      <WeekTableForecast
        week="Semaine 1"
        startDate={startDate}
        rows={[
          {
            label: "Unités vendues",
            editable: true,
            field: "chambresVendues",
          },
          {
            label: "Unités à vendre",
            editable: true,
            field: "chambresAVendre",
          },
          {
            label: "Taux d'occupation",
            editable: false,
            field: "tauxOccupation",
          },
        ]}
        weekData={week1Data}
        onDataChange={(day, updatedDayData) =>
          handleDataChange(1, day, updatedDayData)
        }
      />

      <WeekTableForecast
        week="Semaine 2"
        startDate={addDaysToDate(startDate, 7)}
        rows={[
          {
            label: "Unités vendues",
            editable: true,
            field: "chambresVendues",
          },
          {
            label: "Unités à vendre",
            editable: true,
            field: "chambresAVendre",
          },
          {
            label: "Taux d'occupation",
            editable: false,
            field: "tauxOccupation",
          },
        ]}
        weekData={week2Data}
        onDataChange={(day, updatedDayData) =>
          handleDataChange(2, day, updatedDayData)
        }
      />

      <Button
        variant="contained"
        endIcon={<Check />}
        onClick={handleConfirm}
        disabled={!selectedHotel}
      >
        Confirmer
      </Button>

      <ConfirmationModal
        isOpen={modalOpen}
        status={modalStatus}
        message={modalMessage}
        onClose={() => setModalOpen(false)}
        onConfirm={modalStatus === "confirm" ? handleSend : undefined}
        isLoading={isLoading}
      />

      <Dialog open={excelImportError} onClose={() => setExcelImportError(false)}>
        <DialogTitle>Votre fichier ne semble pas respecter le gabarit</DialogTitle>
        <DialogContent>
          <Typography align="center">Veuillez valider le fichier que vous avez sélectionné</Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={excelImportSuccess} onClose={() => setExcelImportSuccess(false)} >
        <DialogTitle align="center">Données importées avec succès</DialogTitle>
        <DialogContent>
          <Typography align="center">Veuillez valider les données et appuyer sur le bouton confirmer au bas de la page
            pour les sauvegarder.
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importer des prévisions (Excel)</DialogTitle>
        <DialogContent>
          <Alert icon={<InfoOutlinedIcon fontSize="small" />} severity="info" sx={{ mb: 2 }}>
            Utilisez le gabarit de deux semaines pour éviter les erreurs d’import. Les en-têtes et l’ordre des colonnes doivent rester identiques.
          </Alert>
              <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Stack spacing={1}>
                  <Typography variant="subtitle1">Importer vos données</Typography>
                  <Box
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      mt: 1,
                      p: 3,
                      border: "2px dashed",
                      borderColor: dragActive ? "primary.main" : "divider",
                      borderRadius: 2,
                      textAlign: "center",
                      color: "text.secondary",
                      backgroundColor: dragActive ? "action.hover" : "transparent",
                      cursor: "pointer",
                    }}
                    onClick={() => document.getElementById("forecast-excel-file-input")?.click()}
                  >
                    Déposez le fichier ici
                  </Box>
                  <input
                    id="forecast-excel-file-input"
                    type="file"
                    accept=".xlsx"
                    style={{ display: "none" }}
                    onChange={(e) => onFileSelected(e.target.files?.[0] || null)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Format accepté: .xlsx
                  </Typography>
                </Stack>
              </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </FlexColumnBox>
  );
};

export default Previsions;
